import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2, Eye, EyeOff, FileText } from 'lucide-react';
import { pagesApi } from '../../api/endpoints/pages';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { PermissionGate } from '../../components/ui/PermissionGate';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { EmptyState } from '../../components/ui/EmptyState';
import { useToast } from '../../components/ui/Alert';
import { RichTextEditor } from '../../components/ui/RichTextEditor';
import { AiWritingAssistant } from '../../components/ui/AiWritingAssistant';
import { formatDate } from '../../lib/formatters';
import { useStore } from '../../contexts/StoreContext';
import type { StorePage } from '../../types/page';

export function BlogList() {
  const { storeId } = useParams<{ storeId: string }>();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentStore } = useStore();
  const editorBase = import.meta.env.VITE_API_BASE_URL || '';

  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState<StorePage | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<StorePage | null>(null);

  // Form state
  const [formTitle, setFormTitle] = useState('');
  const [formSlug, setFormSlug] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formSeoTitle, setFormSeoTitle] = useState('');
  const [formSeoDesc, setFormSeoDesc] = useState('');
  const [formSeoKeywords, setFormSeoKeywords] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['blog-posts', storeId],
    queryFn: () => pagesApi.listBlogPosts(storeId!),
    enabled: !!storeId,
  });

  const createMutation = useMutation({
    mutationFn: () => pagesApi.create(storeId!, {
      title: formTitle,
      slug: formSlug,
      content: formContent,
      type: 'blog',
      published: false,
      seo: {
        title: formSeoTitle || undefined,
        description: formSeoDesc || undefined,
        keywords: formSeoKeywords || undefined,
      },
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog-posts', storeId] });
      toast('success', t('blog.createSuccess', 'Blog post created'));
      closeModal();
    },
    onError: () => toast('error', t('common.error')),
  });

  const updateMutation = useMutation({
    mutationFn: () => pagesApi.update(storeId!, editTarget!._id, {
      title: formTitle,
      content: formContent,
      seo: {
        title: formSeoTitle || undefined,
        description: formSeoDesc || undefined,
        keywords: formSeoKeywords || undefined,
      },
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog-posts', storeId] });
      toast('success', t('common.success'));
      closeModal();
    },
    onError: () => toast('error', t('common.error')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => pagesApi.delete(storeId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog-posts', storeId] });
      toast('success', t('common.success'));
      setDeleteTarget(null);
    },
    onError: () => toast('error', t('common.error')),
  });

  const togglePublishMutation = useMutation({
    mutationFn: ({ id, published }: { id: string; published: boolean }) =>
      pagesApi.update(storeId!, id, { isPublished: published }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog-posts', storeId] });
      toast('success', t('common.success'));
    },
    onError: () => toast('error', t('common.error')),
  });

  function openCreate() {
    setEditTarget(null);
    setFormTitle(''); setFormSlug(''); setFormContent('');
    setFormSeoTitle(''); setFormSeoDesc(''); setFormSeoKeywords('');
    setShowModal(true);
  }

  function openEdit(post: StorePage) {
    setEditTarget(post);
    setFormTitle(post.title);
    setFormSlug(post.slug || post.pageId || '');
    setFormContent(post.content ?? '');
    setFormSeoTitle(post.seo?.title ?? '');
    setFormSeoDesc(post.seo?.description ?? '');
    setFormSeoKeywords(post.seo?.keywords ?? '');
    setShowModal(true);
  }

  function closeModal() { setShowModal(false); setEditTarget(null); }

  function handleSave() {
    if (!formTitle.trim()) return;
    if (editTarget) updateMutation.mutate();
    else {
      if (!formSlug.trim()) return;
      createMutation.mutate();
    }
  }

  function generateSlug(title: string) {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  const openEditor = (post: StorePage) => {
    const slug = post.slug || post.pageId || post._id;
    window.open(`${editorBase}/editor/${storeId}/${slug}`, '_blank');
  };

  const getStoreUrl = () => {
    if (currentStore?.domain) return `https://${currentStore.domain}`;
    return `https://${currentStore?.id ?? storeId}.00stores.com`;
  };

  const posts = data?.posts ?? [];

  if (isLoading) return <LoadingSpinner fullPage />;

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-bold">{t('blog.title', 'Blog')}</h1>
        <PermissionGate requires="pages.edit">
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4" />
            {t('blog.addPost', 'New Post')}
          </Button>
        </PermissionGate>
      </div>

      {posts.length === 0 ? (
        <EmptyState title={t('blog.empty', 'No blog posts yet')} description={t('blog.emptyDesc', 'Create your first blog post to engage customers.')} />
      ) : (
        <div className="space-y-3">
          {posts.map((post) => (
            <div
              key={post._id}
              className="group rounded-xl border border-border-default bg-bg-secondary p-5 transition-colors hover:border-accent/30"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium truncate">{post.title}</h3>
                    <Badge variant={post.published || post.isPublished ? 'success' : 'default'}>
                      {post.published || post.isPublished ? t('pages.published') : t('pages.draft')}
                    </Badge>
                  </div>
                  <p className="text-xs text-text-muted">
                    /{post.slug || post.pageId}
                    {post.createdAt && ` · ${formatDate(post.createdAt)}`}
                  </p>
                  {post.content && (
                    <p className="text-sm text-text-secondary mt-2 line-clamp-2">
                      {post.content.replace(/<[^>]*>/g, '').slice(0, 200)}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => window.open(`${getStoreUrl()}/blog/${post.slug || post.pageId}`, '_blank')}
                    className="rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-accent"
                    title={t('common.view')}
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <PermissionGate requires="pages.edit">
                    <button
                      onClick={() => openEditor(post)}
                      className="rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-accent"
                      title={t('pages.editor')}
                    >
                      <FileText className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => openEdit(post)}
                      className="rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-accent"
                      title={t('common.edit')}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => togglePublishMutation.mutate({ id: post._id, published: !(post.published || post.isPublished) })}
                      className="rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-warning"
                      title={post.published || post.isPublished ? t('blog.unpublish', 'Unpublish') : t('blog.publish', 'Publish')}
                    >
                      {post.published || post.isPublished ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                    <button
                      onClick={() => setDeleteTarget(post)}
                      className="rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-danger"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </PermissionGate>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={closeModal}
        title={editTarget ? t('blog.editPost', 'Edit Post') : t('blog.addPost', 'New Post')}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={closeModal}>{t('common.cancel')}</Button>
            <Button onClick={handleSave} isLoading={createMutation.isPending || updateMutation.isPending} disabled={!formTitle.trim()}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label={t('common.name')}
            required
            value={formTitle}
            onChange={(e) => {
              setFormTitle(e.target.value);
              if (!editTarget) setFormSlug(generateSlug(e.target.value));
            }}
          />
          {!editTarget && (
            <Input
              label={t('pages.slug')}
              required
              value={formSlug}
              onChange={(e) => setFormSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              dir="ltr"
            />
          )}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm text-text-secondary">{t('blog.content', 'Content')}</label>
              <AiWritingAssistant
                currentContent={formContent}
                itemTitle={formTitle}
                onContentGenerated={setFormContent}
                contentType="blog"
              />
            </div>
            <RichTextEditor
              value={formContent}
              onChange={setFormContent}
              placeholder={t('blog.contentPlaceholder', 'Write your blog post...')}
            />
          </div>

          {/* SEO */}
          <div className="border-t border-border-default pt-4 space-y-3">
            <h3 className="text-sm font-medium text-text-secondary">{t('products.seo')}</h3>
            <Input label={t('settings.metaTitle')} value={formSeoTitle} onChange={(e) => setFormSeoTitle(e.target.value)} />
            <Input label={t('settings.metaDescription')} value={formSeoDesc} onChange={(e) => setFormSeoDesc(e.target.value)} />
            <Input label={t('settings.keywords')} value={formSeoKeywords} onChange={(e) => setFormSeoKeywords(e.target.value)} placeholder="keyword1, keyword2" />
          </div>
        </div>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={t('common.confirm')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>{t('common.cancel')}</Button>
            <Button variant="danger" isLoading={deleteMutation.isPending} onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget._id)}>
              {t('common.delete')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-text-secondary">{t('pages.deleteConfirm')}</p>
        <p className="mt-2 font-medium">{deleteTarget?.title}</p>
      </Modal>
    </div>
  );
}
